'use client';

import path from 'path';

import * as ENGINE from '@gnsx/genesys.js';
import { ASSETS_FOLDER } from '@gnsx/genesys.js';
import { invalidate } from '@react-three/fiber';
import { isEmptyObject, round } from 'es-toolkit';
import * as THREE from 'three';

import { findAssetReferences } from '../assetDependencyAnalyzer';

import { loadActorAsync } from './objectActions';
import { reloadDependentActorsByAsset } from './reloadDependentActors';

import { MESH_COMBINATION_FILE_EXT } from '@/const';
import { actorParenting } from '@/editor/core';
import { attachToGroup, detachGroupChildren, getAttachedGroup } from '@/editor/core/objectHelpers';
import { editorStore, sceneStore } from '@/editor/store';


/**
 * Scene actions - scene/world actions
 */

/**
 * Reload an actor in the world.
 */
export function reloadActorDeferred(actor: ENGINE.Actor, snapshot?: ENGINE.SerializedData) {
  setTimeout(() => {
    void reloadActor(actor, snapshot);
  }, 1);
};

/**
 * Reload an actor in the world with optional snapshot.
 */
export async function reloadActor(actor: ENGINE.Actor, snapshot?: ENGINE.SerializedData): Promise<boolean> {
  const world = actor.getWorld();
  const scene = world?.scene;
  if (!scene) return false;

  // Detach actor from group if previously in one, we'll attach later.
  const attachedGroup = getAttachedGroup(actor);
  if (attachedGroup) {
    detachGroupChildren(attachedGroup, scene);
  }

  // If snapshot is provided, use it to reload the actor, otherwise use the actor's exported object.
  const actorData = (snapshot && !isEmptyObject(snapshot)) ? snapshot : actor.asExportedObject() as ENGINE.SerializedData;

  const uuid = actor.uuid;

  // "Reload" the actor back to the same position.
  const newActor = await loadActorAsync(actorData);
  if (!newActor) {
    console.error('Error reloading actor', actorData);
    return false;
  }
  const didReplaceActor = world.replaceActorByUUID(uuid, newActor);
  if (!didReplaceActor) {
    console.error('[sceneActions.reloadActor] Failed to replace actor', { actorUuid: uuid });
    return false;
  }

  // Refresh selected object.
  const selectedObject = sceneStore.selectedObjects.find(obj => obj.id === uuid);
  if (selectedObject) {
    selectedObject.init(newActor);
  }

  // Attach actor back to group if previous in one.
  if (attachedGroup) {
    attachToGroup(attachedGroup, [newActor]);
  }

  // Refresh outliner (use pre-reload uuid — loaded actor may not match outliner node ids yet).
  sceneStore.outliner.getNodeById(uuid)?.init(newActor);
  sceneStore.updateSelectionGroup();
  actorParenting.validateAndRepairAttachments(world);
  invalidate();

  return true;
};

/**
 * Hide or show an actor in the editor scene.
 */
export function hideActor(actor: ENGINE.Actor, hide: boolean) {
  sceneStore.outliner.getNodeById(actor.uuid)?.setVisibilityInEditor(!hide);
}

/**
 * Focus on selected objects.
 */
export function focusSelectedObjects() {
  const focusEvent = new KeyboardEvent('keydown', { key: 'f', code: 'KeyF', keyCode: 70, which: 70, bubbles: true, cancelable: true });
  window.dispatchEvent(focusEvent);
}

/**
 * Select an actor by its uuid.
 */
export function selectActorById(uuid: string) {
  const actor = sceneStore.world?.getActorsByPredicate((actor: ENGINE.Actor) => actor.uuid === uuid)[0];
  if (actor) {
    sceneStore.selectObject(actor);
  }
  return actor;
}


/**
 * Select multiple actors by their uuids.
 */
export function selectActorsByIds(uuids: string[]) {
  const actors = sceneStore.world?.getActorsByPredicate((actor: ENGINE.Actor) => uuids.includes(actor.uuid));
  if (actors) {
    sceneStore.selectObjects(actors);
  }
}

export interface MeshCombinationMeshInstance {
  path: string;
  position: [number, number, number];
  quaternion: [number, number, number, number];
  rotation?: [number, number, number]; // Optional Euler rotation in radians
  scale: [number, number, number];
  asRoot?: boolean;
}

export interface MeshCombinationActorInstance {
  meshes: MeshCombinationMeshInstance[];
  name?: string;
  description?: string;
}

export interface MeshCombination {
  actors: MeshCombinationActorInstance[];
}

export async function exportMeshCombinations(name: string = '') {
  if (!name) {
    const currentScene = editorStore.project.currentScene;
    if (!currentScene) {
      name = 'mesh-combination';
    } else {
      name = path.basename(currentScene, path.extname(currentScene));
    }
  }

  console.log('Exporting mesh combinations to', name);
  const actors = sceneStore.world?.getActors(ENGINE.Actor);
  const meshCombinationData: MeshCombination = { actors: [] };
  if (!actors) return meshCombinationData;
  for (const actor of actors) {
    // if (actor.editorData?.meshCombinationImportFile) {
    //   continue; // Skip actors that are not mesh combinations.
    // }

    const components = actor.getComponents(ENGINE.ModelMeshComponent);
    const actorInstanceData: MeshCombinationActorInstance = {
      meshes: [],
      name: actor.name,
      description: actor.editorData?.description,
    };

    for (const component of components) {
      const modelUrl = component.modelUrl ?? '';
      const position = actor.getWorldPosition().toArray();
      const quaternion = actor.getWorldQuaternion().toArray();
      const scale = actor.getWorldScale().toArray();
      actorInstanceData.meshes.push({
        path: modelUrl,
        position: position as [number, number, number],
        quaternion: [quaternion[0], quaternion[1], quaternion[2], quaternion[3]],
        scale: scale as [number, number, number],
        asRoot: component === actor.rootComponent ? true : undefined
      });
    }

    if (actorInstanceData.meshes.length > 0) {
      meshCombinationData.actors.push(actorInstanceData);
    }
  }

  const storageProvider = editorStore.project.makeScopedStorageProvider();

  function roundNumbersReplacer(_key: string, value: unknown) {
    if (typeof value === 'number') {
      return round(value, 5);
    }
    return value;
  }

  await storageProvider.uploadFile(
    ENGINE.AssetPath.fromString(`${ENGINE.PROJECT_PATH_PREFIX}/${ASSETS_FOLDER}/mesh-combinations/${name}${MESH_COMBINATION_FILE_EXT}`)
    , JSON.stringify(meshCombinationData, roundNumbersReplacer, 2)
  );
}

export async function importMeshCombinations(file: string) {
  const storageProvider = editorStore.project.makeScopedStorageProvider();
  const content = await storageProvider.downloadFileAsJson<MeshCombination>(ENGINE.AssetPath.fromString(file));
  if (!content || !content.actors || !Array.isArray(content.actors)) {
    console.error('Invalid mesh combination file:', file);
    return;
  }

  const actorsData: MeshCombinationActorInstance[] = content.actors;

  // Group all meshes by their unique model URL across all actors
  const meshInstancesByModel = new Map<string, Array<{
    position: THREE.Vector3;
    rotation: THREE.Euler;
    scale: THREE.Vector3;
    actorData?: MeshCombinationActorInstance;
  }>>();

  // Collect all mesh instances grouped by model URL
  for (const actorData of actorsData) {
    for (const meshData of actorData.meshes) {
      const modelUrl = meshData.path;

      // Check if model exists (for project/engine paths)
      if (modelUrl.startsWith(ENGINE.ENGINE_PATH_PREFIX) || modelUrl.startsWith(ENGINE.PROJECT_PATH_PREFIX)) {
        if (await storageProvider.exists(ENGINE.AssetPath.fromString(modelUrl)) === false) {
          console.warn(`Mesh model not found: ${modelUrl}`);
          continue; // Skip if the model does not exist.
        }
      }

      // Handle both rotation (Euler) and quaternion formats
      const position = new THREE.Vector3(...(meshData.position || [0, 0, 0]));
      const scale = new THREE.Vector3(...(meshData.scale || [1, 1, 1]));

      let rotation: THREE.Euler;
      if (meshData.rotation && Array.isArray(meshData.rotation)) {
        // Direct Euler angles (x, y, z in radians)
        rotation = new THREE.Euler(...meshData.rotation);
      } else if (meshData.quaternion && Array.isArray(meshData.quaternion)) {
        // Quaternion format (x, y, z, w)
        rotation = new THREE.Euler().setFromQuaternion(new THREE.Quaternion(...meshData.quaternion));
      } else {
        // Default: no rotation
        rotation = new THREE.Euler(0, 0, 0);
      }

      // Add to the model's instance list
      if (!meshInstancesByModel.has(modelUrl)) {
        meshInstancesByModel.set(modelUrl, []);
      }
      meshInstancesByModel.get(modelUrl)!.push({
        position,
        rotation,
        scale,
        actorData
      });
    }
  }

  // Create one actor per unique model URL with an InstancedModelMeshComponent
  const actors: ENGINE.Actor[] = [];
  for (const [modelUrl, instances] of meshInstancesByModel) {
    console.log(`Creating instanced actor for model: ${modelUrl} with ${instances.length} instances`);

    // Create the instanced component with all transforms
    const instancedComponent = ENGINE.InstancedModelMeshComponent.create({
      modelUrl,
      maxInstances: Math.max(instances.length, 100), // Ensure at least 100 for future additions
      instances: instances.map(inst => ({
        position: inst.position,
        rotation: inst.rotation,
        scale: inst.scale
      })),
      isWorldSpace: true, // Use world space for transforms
    });

    // Create actor with the instanced component
    const actor = ENGINE.Actor.create({
      rootComponent: instancedComponent,
    });

    // Use the first instance's actor data for naming, or create a default name
    const firstInstanceActorData = instances[0]?.actorData;
    const modelName = path.basename(modelUrl, path.extname(modelUrl));
    actor.editorData.description = firstInstanceActorData?.description
      || `Instanced mesh with ${instances.length} instances of ${modelName}`;
    actor.editorData.meshCombinationImportFile = file;
    actor.setName(firstInstanceActorData?.name
      ? `${firstInstanceActorData.name} (Instanced)`
      : `${modelName} (${instances.length} instances)`);

    actors.push(actor);
  }

  // Remove previously imported mesh combinations
  removeImportedMeshCombinations(false);

  // Add all instanced actors to the world
  if (actors.length > 0) {
    sceneStore.world?.addActors(...actors);
    console.log(`Imported ${actors.length} instanced actors with total meshes from mesh combination`);
  }

  sceneStore.updateOutliner();
}

export function removeImportedMeshCombinations(refreshOutliner: boolean = true) {
  const existingActors = sceneStore.world?.getActorsByPredicate((actor: ENGINE.Actor) => {
    return !!actor.editorData?.meshCombinationImportFile;
  });

  if (existingActors && existingActors.length > 0) {
    sceneStore.world?.removeActors(...existingActors);

    if (refreshOutliner) {
      sceneStore.updateSelectionGroup();
    }
  }
}

/**
 * Reload actors in the world that reference a given asset path.
 */
export async function reloadActorsReferencingAsset(assetPath: string): Promise<number> {
  const world = sceneStore.world;
  if (!world) return 0;

  const actors = world.getActors(ENGINE.Actor);
  if (!actors || actors.length === 0) return 0;

  return await reloadDependentActorsByAsset(assetPath, {
    actors,
    findReferences: findAssetReferences,
    reloadActor: async (actor, snapshot) => await reloadActor(actor as ENGINE.Actor, snapshot),
    updateOutliner: () => sceneStore.updateOutliner(),
  });
}
